// AssetPicker — canonical `asset-picker` field primitive (DRIFT-910 slice 4,
// CHG-2483). Returns the chosen Asset.id (string) or null.
//
// v2 implementation: a self-fetching wrapper over the a11y-complete SelectField
// (asset chosen by title), plus a thumbnail preview of the current selection.
// The v1 reference (web/dashboard/src/components/forms/AssetPicker.tsx, CHG-1186)
// is a 486-LOC modal gallery with org/platform tabs, search, inline upload, and
// multi-select; this slice ships the focused single-select form needed by the
// event hero-image slot (NOT a verbatim port, per the brief). A richer gallery
// modal can follow if a use case needs it. Options are unfiltered by kind (the
// list endpoint takes a single kind; not narrowing avoids hiding valid
// photo/illustration hero candidates); name-sorted.
//
// Contract: specs/_product/field-primitive-registry.md §1 ('asset-picker').
// Doctrine: feedback_field_primitive_components_canonical.md. a11y is inherited
// 1:1 from SelectField (label htmlFor, aria-invalid/required, aria-describedby);
// the preview <img> carries the asset's alt_text/title as its alt.

import { useQuery } from "@tanstack/react-query";
import type { FieldPrimitiveProps } from "./types";
import { SelectField, type SelectOption } from "./SelectField";
import { assetsApi } from "../../api/assets";

export function AssetPicker(props: FieldPrimitiveProps<string>) {
  const { data, isLoading } = useQuery({
    queryKey: ["assets", "picker-options"],
    queryFn: () =>
      assetsApi.listAssets({ pageSize: 200, sortField: "title", sortDirection: "asc" }),
  });
  const items = data?.items ?? [];
  const options: SelectOption[] = items.map((a) => ({
    value: a.id,
    label: a.title || `Asset ${a.id.slice(0, 8)}`,
  }));
  const selected = items.find((a) => a.id === props.value);
  const previewSrc = selected?.thumbnail_url || selected?.primary_url || null;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <SelectField
        {...props}
        options={options}
        placeholder={isLoading ? "Loading assets…" : "Select asset…"}
        disabled={props.disabled || isLoading}
      />
      {previewSrc ? (
        <img
          src={previewSrc}
          alt={selected?.alt_text || selected?.title || "Selected asset preview"}
          style={{
            width: 96,
            height: 96,
            objectFit: "cover",
            borderRadius: "var(--radius-md)",
            border: "1px solid var(--color-border)",
          }}
        />
      ) : null}
    </div>
  );
}
